// A status card for one in-memory review session on the entry screen. Shows the
// session name, a status badge, its word + conflict counts, a noyau dot, the
// creation date, and Reprendre / Supprimer actions. The card stays black & white
// except the Reprendre button, which carries the session's noyau accent (black
// when indéterminé) as a hint of the batch's colour.
import { accentStyle, noyauDotKey } from '@/lib/bulk-add/accent';
import { detectConflicts } from '@/lib/bulk-add/conflicts';
import type { BulkSession, BulkSessionStatus } from '@/lib/bulk-add/types';
import { useMemo } from 'react';

interface BulkSessionCardProps {
  session: BulkSession;
  onResume: () => void;
  onDelete: () => void;
}

/** Human label + badge modifier for each session status. */
const STATUS_META: Record<BulkSessionStatus, { label: string; modifier: string }> = {
  input: { label: 'Brouillon', modifier: 'input' },
  analyzing: { label: 'Analyse en cours', modifier: 'analyzing' },
  ready: { label: 'À réviser', modifier: 'ready' },
  reviewing: { label: 'En cours', modifier: 'reviewing' },
  error: { label: 'Erreur', modifier: 'error' },
};

/** Entry-screen card for a single review session. */
export function BulkSessionCard({ session, onResume, onDelete }: BulkSessionCardProps) {
  const meta = STATUS_META[session.status];
  const noyauKey = noyauDotKey(session.noyau);
  const activeCount = session.words.filter((w) => !w.excluded).length;
  const conflictCount = useMemo(
    () => detectConflicts(session.words, new Set(session.resolvedKeys)).length,
    [session.words, session.resolvedKeys],
  );

  return (
    <div className="bulk-card">
      <div className="bulk-card__top">
        <span
          className={`bulk-card__dot ${noyauKey ? `words-noyau-dot--${noyauKey}` : 'words-noyau-dot--none'}`}
          aria-hidden="true"
        />
        <span className={`bulk-badge bulk-badge--${meta.modifier}`}>{meta.label}</span>
      </div>

      <h2 className="bulk-card__name">{session.name}</h2>

      <p className="bulk-card__stats">
        {activeCount} mot{activeCount > 1 ? 's' : ''}
        {conflictCount > 0 && ` · ${conflictCount} conflit${conflictCount > 1 ? 's' : ''}`}
      </p>

      <div className="bulk-card__actions">
        <button
          type="button"
          className="bulk-btn bulk-btn--primary bulk-btn--sm"
          style={accentStyle(session.noyau)}
          onClick={onResume}
        >
          Reprendre
        </button>
        <button
          type="button"
          className="bulk-card__delete"
          aria-label={`Supprimer ${session.name}`}
          onClick={onDelete}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            delete
          </span>
        </button>
      </div>
    </div>
  );
}
